import { useColorModeValue } from "@/components/ui/color-mode"
import { effects } from "@/theme"
import { Box, type BoxProps } from "@chakra-ui/react"
import { forwardRef } from "react"

export interface VisionCardProps extends BoxProps {
  variant?: "elevated" | "outline" | "solid" | "gradient"
  glassMorphism?: boolean
}

export const VisionCard = forwardRef<HTMLDivElement, VisionCardProps>(
  ({ variant = "elevated", glassMorphism = true, ...props }, ref) => {
    const isLight = useColorModeValue(true, false)

    const darkStyles = {
      elevated: {
        bg: effects.glass.cardBg,
        backdropFilter: "blur(120px)",
        borderColor: "rgba(255, 255, 255, 0.16)",
        boxShadow: effects.shadows.card,
      },
      outline: {
        bg: "transparent",
        backdropFilter: "blur(20px)",
        borderColor: "rgba(255, 255, 255, 0.16)",
        boxShadow: "none",
      },
      solid: {
        bg: "rgba(15, 21, 44, 0.94)",
        backdropFilter: "blur(20px)",
        borderColor: "transparent",
        boxShadow: "none",
      },
      gradient: {
        bg: effects.gradients.cardDark,
        backdropFilter: "blur(120px)",
        borderColor: "rgba(255, 255, 255, 0.16)",
        boxShadow: effects.shadows.card,
      },
    }

    const lightStyles = {
      elevated: {
        bg: "white",
        backdropFilter: "none",
        borderColor: "#E2E8F0",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.06)",
      },
      outline: {
        bg: "transparent",
        backdropFilter: "none",
        borderColor: "#E2E8F0",
        boxShadow: "none",
      },
      solid: {
        bg: "#f7f8fc",
        backdropFilter: "none",
        borderColor: "transparent",
        boxShadow: "none",
      },
      gradient: {
        bg: "white",
        backdropFilter: "none",
        borderColor: "#E2E8F0",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.06)",
      },
    }

    const styles = isLight
      ? lightStyles[variant] || lightStyles.elevated
      : darkStyles[variant] || darkStyles.elevated

    return (
      <Box
        ref={ref}
        w="100%"
        p="22px"
        borderRadius="20px"
        border="1px solid"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: "translateY(-4px)",
          boxShadow: isLight
            ? "0px 8px 30px rgba(0, 0, 0, 0.1)"
            : "0px 25px 60px rgba(112, 144, 176, 0.15)",
        }}
        {...styles}
        {...props}
      />
    )
  },
)

VisionCard.displayName = "VisionCard"
